import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';
import {
  ProgramApiService,
  LastSessionDetails,
} from '../../services/api/ProgramApiService';

interface EditSessionDialogProps {
  open: boolean;
  onClose: () => void;
  enrollmentId: number;
  lastSession: LastSessionDetails;
  onSessionEdited: () => void;
}

type ExerciseStatus = 'success' | 'fail';

export const EditSessionDialog: React.FC<EditSessionDialogProps> = ({
  open,
  onClose,
  enrollmentId,
  lastSession,
  onSessionEdited,
}) => {
  const { t } = useLanguage();

  const [entries, setEntries] = useState<Record<number, ExerciseStatus>>(() =>
    Object.fromEntries(
      lastSession.exercises.map((ex) => [
        ex.program_exercise_id,
        ex.succeeded ? 'success' : 'fail',
      ])
    )
  );
  const [notes, setNotes] = useState(lastSession.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusChange = (exerciseId: number, status: ExerciseStatus) => {
    if (!status) return;
    setEntries((prev) => ({ ...prev, [exerciseId]: status }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await ProgramApiService.editLastSession(enrollmentId, {
        notes: notes.trim() || undefined,
        results: Object.entries(entries).map(([id, status]) => ({
          program_exercise_id: Number(id),
          succeeded: status === 'success',
        })),
      });
      onSessionEdited();
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to edit session');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {t('programs.editSession')} — {lastSession.template_name}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {new Date(lastSession.session_date).toLocaleDateString()}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {lastSession.exercises.map((ex) => (
          <Box key={ex.program_exercise_id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body1">{ex.exercise_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ex.sets}×{ex.reps} @ {ex.weight_used_kg} kg
              </Typography>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={entries[ex.program_exercise_id] ?? 'success'}
              onChange={(_, val) => handleStatusChange(ex.program_exercise_id, val)}
            >
              <ToggleButton value="success" color="success">
                <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('programs.succeeded')}
              </ToggleButton>
              <ToggleButton value="fail" color="error">
                <CloseIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('programs.failed')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ))}

        <TextField
          label={t('programs.notesOptional')}
          multiline
          rows={2}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('programs.editSession')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
